import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FormControlLabel, Grid, MenuItem, Switch, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { getDepartments, getTitles } from '../../../../api/endpoints/corporate';
import { addCorporateEmployee, editCorporateEmployee } from '../../../../api/endpoints/corporateEmployee';
import type { Department, Title } from '../../../../api/types/corporate';
import type {
  CorporateEmployee,
  EmployeeCostCenterOption,
  EmployeePolicyOption,
} from '../../../../api/types/corporateEmployee';
import { FormDialog } from '../../../../components/common';
import { PhoneField } from '../../../../components/PhoneField';

const schema = z.object({
  name: z.string().min(1, 'Ad gerekli'),
  surName: z.string().min(1, 'Soyad gerekli'),
  gender: z.string().optional(),
  dateOfBirth: z.string().min(1, 'Doğum tarihi gerekli'),
  emailAdress: z.string().min(1, 'E-posta gerekli').email('Geçerli bir e-posta girin'),
  phoneNumber: z.string().optional(),
  countryOfResidance: z.string().optional(),
  cityOfResidance: z.string().optional(),
  policyID: z.coerce.number().min(1, 'Politika seçimi gerekli'),
  costCenterID: z.coerce.number().min(1, 'Masraf merkezi seçimi gerekli'),
  departmentId: z.coerce.number(),
  titleId: z.coerce.number(),
  registrationNumber: z.string().optional(),
  isActive: z.boolean(),
  /** "Personel Satın Alma Yapabilir?" — portal kullanıcısı oluşturulur. */
  isProgramUser: z.boolean(),
  /** "Seyahat Planlamacısı Mı?" — planlamacı kullanıcısı oluşturulur. */
  isSp: z.boolean(),
}).refine((v) => !(v.isProgramUser && v.isSp), {
  // Backend `CorporateEmployeeWorker.AddCorporateEmployee` bu ikisini birlikte reddediyor.
  message: 'Program kullanıcı ve seyahat planlamacısı aynı anda atanamaz.',
  path: ['isSp'],
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = {
  name: '',
  surName: '',
  gender: '',
  dateOfBirth: '',
  emailAdress: '',
  phoneNumber: '',
  countryOfResidance: '',
  cityOfResidance: '',
  policyID: 0,
  costCenterID: 0,
  departmentId: 0,
  titleId: 0,
  registrationNumber: '',
  isActive: true,
  isProgramUser: false,
  isSp: false,
};

/** ISO date-time -> `YYYY-MM-DD` for the `type="date"` input. */
function toDateInputValue(iso?: string): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

function toFormValues(emp: CorporateEmployee): FormValues {
  return {
    name: emp.name ?? '',
    surName: emp.surName ?? '',
    gender: emp.gender ?? '',
    dateOfBirth: toDateInputValue(emp.dateOfBirth),
    emailAdress: emp.emailAdress ?? '',
    phoneNumber: emp.phoneNumber ?? '',
    countryOfResidance: emp.countryOfResidance ?? '',
    cityOfResidance: emp.cityOfResidance ?? '',
    policyID: emp.policyID ?? 0,
    costCenterID: emp.costCenterID ?? 0,
    departmentId: emp.departmentId ?? 0,
    titleId: emp.titleId ?? 0,
    registrationNumber: emp.registrationNumber ?? '',
    isActive: emp.isActive,
    isProgramUser: emp.isProgramUser ?? false,
    isSp: emp.isSp ?? false,
  };
}

/** Create/edit dialog for Personel. On edit, the request MUST carry forward every field from
 *  the original `employee` (managerID, kvkkAccepted*, isProgramUser, ...) — the real API's
 *  EditCorporateEmployee throws a generic EF save error if `kvkkAcceptedAt` (a DateTime
 *  field) is omitted and defaults to `0001-01-01` (before SQL Server's `datetime` minimum).
 *  See api/types/corporateEmployee.ts for the full list of confirmed real-API quirks. */
export function EmployeeFormDialog({
  open,
  employee,
  policies,
  costCenters,
  onClose,
}: {
  open: boolean;
  /** null = create mode; otherwise edit mode for this row. */
  employee: CorporateEmployee | null;
  policies: EmployeePolicyOption[];
  costCenters: EmployeeCostCenterOption[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!employee;

  const departmentsQuery = useQuery({
    queryKey: ['corporate', 'departments'],
    queryFn: () => getDepartments(),
    enabled: open,
  });
  const titlesQuery = useQuery({
    queryKey: ['corporate', 'titles'],
    queryFn: () => getTitles(),
    enabled: open,
  });
  const departments: Department[] = departmentsQuery.data?.departments ?? [];
  const titles: Title[] = titlesQuery.data?.titles ?? [];

  const {
    control,
    handleSubmit,
    register,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: emptyValues,
  });

  useEffect(() => {
    if (open) reset(employee ? toFormValues(employee) : emptyValues);
  }, [open, employee, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      const dateOfBirth = values.dateOfBirth ? `${values.dateOfBirth}T00:00:00` : undefined;
      const departmentId = values.departmentId || null;
      const titleId = values.titleId || null;
      if (isEdit && employee) {
        return editCorporateEmployee({ ...employee, ...values, dateOfBirth, departmentId, titleId });
      }
      return addCorporateEmployee({ ...values, dateOfBirth, departmentId, titleId });
    },
    onSuccess: () => {
      enqueueSnackbar(isEdit ? 'Personel güncellendi.' : 'Personel oluşturuldu.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['corporateEmployee', 'list'] });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));

  return (
    <FormDialog
      open={open}
      title={isEdit ? 'Personeli Güncelle' : 'Yeni Personel'}
      onClose={onClose}
      onSubmit={submit}
      submitting={mutation.isPending}
      error={mutation.error}
      maxWidth="md"
    >
      <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Ad"
                error={!!errors.name}
                helperText={errors.name?.message}
                {...register('name')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Soyad"
                error={!!errors.surName}
                helperText={errors.surName?.message}
                {...register('surName')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Cinsiyet"
                error={!!errors.gender}
                helperText={errors.gender?.message}
                {...register('gender')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Doğum Tarihi"
                slotProps={{ inputLabel: { shrink: true } }}
                error={!!errors.dateOfBirth}
                helperText={errors.dateOfBirth?.message}
                {...register('dateOfBirth')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="E-posta"
                error={!!errors.emailAdress}
                helperText={errors.emailAdress?.message}
                {...register('emailAdress')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="phoneNumber"
                render={({ field }) => (
                  <PhoneField
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    label="Telefon"
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Ülke"
                error={!!errors.countryOfResidance}
                helperText={errors.countryOfResidance?.message}
                {...register('countryOfResidance')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Şehir"
                error={!!errors.cityOfResidance}
                helperText={errors.cityOfResidance?.message}
                {...register('cityOfResidance')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="policyID"
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    size="small"
                    label="Politika"
                    error={!!errors.policyID}
                    helperText={errors.policyID?.message}
                  >
                    <MenuItem value={0} disabled>
                      Seçiniz
                    </MenuItem>
                    {policies.map((p) => (
                      <MenuItem key={p.policyID} value={p.policyID}>
                        {p.policyName}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="costCenterID"
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    size="small"
                    label="Masraf Merkezi"
                    error={!!errors.costCenterID}
                    helperText={errors.costCenterID?.message}
                  >
                    <MenuItem value={0} disabled>
                      Seçiniz
                    </MenuItem>
                    {costCenters.map((c) => (
                      <MenuItem key={c.costCenterID} value={c.costCenterID}>
                        {c.costCenterName}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="departmentId"
                render={({ field }) => (
                  <TextField {...field} select fullWidth size="small" label="Departman">
                    <MenuItem value={0}>Yok</MenuItem>
                    {departments.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="titleId"
                render={({ field }) => (
                  <TextField {...field} select fullWidth size="small" label="Unvan">
                    <MenuItem value={0}>Yok</MenuItem>
                    {titles.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Sicil No"
                error={!!errors.registrationNumber}
                helperText={errors.registrationNumber?.message}
                {...register('registrationNumber')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <Controller
                control={control}
                name="isActive"
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                    label="Aktif"
                  />
                )}
              />
            </Grid>
            {/* `isProgramUser` ile `isSp` karşılıklı dışlayıcı — biri açılınca öteki kapanır
                (legacy `Create.cshtml` JS + backend `AddCorporateEmployee` kontrolü). */}
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <Controller
                control={control}
                name="isProgramUser"
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                          if (e.target.checked) setValue('isSp', false);
                        }}
                      />
                    }
                    label="Personel Satın Alma Yapabilir?"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <Controller
                control={control}
                name="isSp"
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                          if (e.target.checked) setValue('isProgramUser', false);
                        }}
                      />
                    }
                    label="Seyahat Planlamacısı Mı?"
                  />
                )}
              />
            </Grid>
            {errors.isSp?.message && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="error">
                  {errors.isSp.message}
                </Typography>
              </Grid>
            )}
      </Grid>
    </FormDialog>
  );
}
